import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Stack,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { ChevronDown, ChevronRight } from 'react-feather';
import { RichTextContent } from '../../../components/common/RichTextEditor/RichTextContent';
import {
  SectionNumber,
  ACCORDION_BORDER,
  ROW_EXPANDED_BG,
} from '../../cohorts/components/sessions/agendaSectionPrimitives';
import type {
  PreSessionReflectionPayload,
  ReflectionStatus,
} from '../../../services/api/preSessionReflection';
import { usePriorCommitmentReflectionAutosave } from '../preSessionReflection/usePriorCommitmentReflectionAutosave';

const TITLE_COLOR = '#1A1A2E';

const STATUS_LABELS: Record<ReflectionStatus, string> = {
  NOT_STARTED: 'NOT STARTED',
  IN_PROGRESS: 'IN PROGRESS',
  COMPLETED: 'COMPLETED',
};
const STATUS_COLORS: Record<ReflectionStatus, string> = {
  NOT_STARTED: 'gray',
  IN_PROGRESS: 'warningAmber',
  COMPLETED: 'medleyGreen',
};

interface PeerLeaderReflectionAccordionProps {
  sessionId: string | undefined;
  reflection: PreSessionReflectionPayload;
  /** Live reflection status (recomputed by the page's autosave). */
  reflectionStatus: ReflectionStatus;
  /** "Saving…" hint while the reflection autosave is in flight. */
  saveHint?: string;
  onQuestionChange: (questionId: string, responseText: string) => void;
  onSaveAllReflection: (
    pending: Array<{ question_id: string; response_text: string }>
  ) => Promise<void>;
  /** Persist the Reading / Tasks reviewed marker; resolves when saved. */
  onMarkReadingReviewed: () => Promise<void>;
  /**
   * MED-1217 (AC3/AC4) — request that the page collapse the OUTER "Your
   * Pre-Session Reflection" step. Called only in response to an explicit
   * completion action (Reflection Questions Save / Reading Mark as Reviewed) and
   * only once this accordion has confirmed every present inner row is complete.
   * Never fired from a background autosave, so the step can't collapse mid-edit.
   */
  onOuterStepComplete: () => void;
}

/**
 * MED-1115 follow-up — peer-leader-only nested accordion for the "Your
 * Pre-Session Reflection" step. Mirrors the Session Guide's agenda accordion
 * (`PeerLeaderAgendaSectionList`): one outlined container with numbered,
 * collapsible rows, reusing the shared `agendaSectionPrimitives` so the two
 * peer accordions read identically.
 *
 * Deliberately NOT the shared participant cards (ReflectionFormCard /
 * PriorCommitmentCard / ReadingTasksCard) — the peer step needs the nested
 * accordion treatment, and a self-contained row avoids duplicating the step's
 * "Your Pre-Session Reflection" header. The participant page keeps the shared
 * cards. Only the shared autosave hooks + visual primitives are reused.
 *
 * Rows (numbered among those present): Reflect on Prior Commitments (when a
 * prior commitment exists) → Reflection Questions → Reading / Tasks (when
 * authored). Each seeds collapsed from its completion (no flash) and collapses
 * on its completion action; manual re-expand always allowed.
 */
export function PeerLeaderReflectionAccordion({
  sessionId,
  reflection,
  reflectionStatus,
  saveHint,
  onQuestionChange,
  onSaveAllReflection,
  onMarkReadingReviewed,
  onOuterStepComplete,
}: PeerLeaderReflectionAccordionProps) {
  const hasCommitment = !!reflection.prior_commitment;
  // MED-1226 (AC5) — when there's no real commitment block yet but the section
  // is configured and the prior session hasn't completed, show an informational
  // "available later" row instead of hiding the section. Mutually exclusive with
  // a real block (the backend guarantees prior_commitment_pending is false when
  // prior_commitment is non-null).
  const showCommitmentPending =
    !hasCommitment && !!reflection.prior_commitment_pending;
  const showCommitmentRow = hasCommitment || showCommitmentPending;
  const hasReading = !!reflection.reading_tasks;

  // Number the rows that are actually present (1..N), like the agenda sections.
  let n = 0;
  const commitmentN = showCommitmentRow ? ++n : 0;
  const questionsN = ++n;
  const readingN = hasReading ? ++n : 0;

  // MED-1217 (AC4/AC5) — the prior-commitment row autosaves entirely inside
  // itself and has no button, so the page/accordion otherwise can't see its
  // response. We lift its LIVE (just-typed, pre-debounce) non-empty state up via
  // a callback so the outer-collapse gate reads the current textarea value — not
  // a stale server snapshot that could lag ~800ms behind and wrongly read empty.
  const [priorCommitmentComplete, setPriorCommitmentComplete] = useState(
    () =>
      !hasCommitment ||
      (reflection.prior_commitment?.reflection_text.trim().length ?? 0) > 0
  );
  const handlePriorCommitmentCompleteness = useCallback((nonEmpty: boolean) => {
    setPriorCommitmentComplete(nonEmpty);
  }, []);

  // MED-1217 (AC3) — is every PRESENT inner row complete? A row that isn't shown
  // does not gate. `overrides` lets an explicit action supply its own row's fresh
  // result (the just-saved / just-reviewed truth) rather than waiting for the
  // prop to round-trip through the page.
  const allInnerRowsComplete = useCallback(
    (overrides: {
      questionsComplete?: boolean;
      readingComplete?: boolean;
    }): boolean => {
      const questionsComplete =
        overrides.questionsComplete ?? reflectionStatus === 'COMPLETED';
      const readingComplete =
        !hasReading ||
        (overrides.readingComplete ??
          reflection.reading_tasks_reviewed_at != null);
      const commitmentComplete = !hasCommitment || priorCommitmentComplete;
      return questionsComplete && readingComplete && commitmentComplete;
    },
    [
      reflectionStatus,
      hasReading,
      hasCommitment,
      priorCommitmentComplete,
      reflection.reading_tasks_reviewed_at,
    ]
  );

  // MED-1217 (AC4) — explicit completion actions. The Reflection Questions Save
  // reports whether all questions are now answered; Reading reports its review.
  // Each collapses the outer step only if that makes ALL present rows complete.
  const handleQuestionsSaved = useCallback(
    (allAnswered: boolean) => {
      if (allInnerRowsComplete({ questionsComplete: allAnswered })) {
        onOuterStepComplete();
      }
    },
    [allInnerRowsComplete, onOuterStepComplete]
  );
  const handleReadingReviewed = useCallback(() => {
    if (allInnerRowsComplete({ readingComplete: true })) {
      onOuterStepComplete();
    }
  }, [allInnerRowsComplete, onOuterStepComplete]);

  return (
    <Box
      style={{
        border: `1px solid ${ACCORDION_BORDER}`,
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {hasCommitment && reflection.prior_commitment && (
        <PriorCommitmentRow
          n={commitmentN}
          isLast={false}
          sessionId={sessionId}
          commitmentText={reflection.prior_commitment.commitment_text}
          initialReflection={reflection.prior_commitment.reflection_text}
          isLocked={reflection.is_locked}
          onCompletenessChange={handlePriorCommitmentCompleteness}
        />
      )}

      {/* MED-1226 (AC5) — prep opened before the prior session completed, so the
        commitment data can't exist yet. Keep the section visible with an
        "available later" message rather than hiding it, so the leader knows why
        it isn't ready. Purely informational: no textarea, no autosave, and it
        does not gate the outer step's completion (hasCommitment stays false). */}
      {showCommitmentPending && (
        <PriorCommitmentPendingRow n={commitmentN} isLast={false} />
      )}

      <QuestionsRow
        n={questionsN}
        isLast={!hasReading}
        questions={reflection.questions}
        status={reflectionStatus}
        estimatedMinutes={reflection.estimated_minutes}
        isLocked={reflection.is_locked}
        saveHint={saveHint}
        onChange={onQuestionChange}
        onSaveAll={onSaveAllReflection}
        onSaved={handleQuestionsSaved}
      />

      {hasReading && reflection.reading_tasks && (
        <ReadingRow
          n={readingN}
          isLast
          content={reflection.reading_tasks}
          reviewedAt={reflection.reading_tasks_reviewed_at}
          onMarkReviewed={onMarkReadingReviewed}
          onReviewed={handleReadingReviewed}
        />
      )}
    </Box>
  );
}

/**
 * Shared row shell — mirrors a `PeerLeaderAgendaSectionList` SectionRow: a
 * numbered, clickable header (number circle + title + optional left badge) with
 * optional right-aligned meta and a chevron, over a controlled Collapse body.
 */
function AccordionRow({
  n,
  title,
  open,
  onToggle,
  isLast,
  titleBadge,
  headerRight,
  children,
}: {
  n: number;
  title: string;
  open: boolean;
  onToggle: () => void;
  isLast: boolean;
  /** Completion badge shown immediately after the title (grail style). */
  titleBadge?: ReactNode;
  /** Right-aligned meta (e.g. the question/minutes count) before the chevron. */
  headerRight?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        borderBottom: isLast ? undefined : `1px solid ${ACCORDION_BORDER}`,
        background: open ? ROW_EXPANDED_BG : undefined,
      }}
    >
      <UnstyledButton
        onClick={onToggle}
        style={{
          display: 'block',
          width: '100%',
          padding: 'var(--mantine-spacing-md) var(--mantine-spacing-lg)',
        }}
        aria-expanded={open}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group align="center" gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
            <SectionNumber n={n} active={open} />
            <Text fw={600} c={TITLE_COLOR}>
              {title}
            </Text>
            {titleBadge}
          </Group>
          <Group gap="sm" align="center" wrap="nowrap">
            {headerRight}
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </Group>
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        <Stack
          gap="md"
          style={{
            padding: '0 var(--mantine-spacing-lg) var(--mantine-spacing-md)',
          }}
        >
          {children}
        </Stack>
      </Collapse>
    </div>
  );
}

/**
 * Shared "grail" completion badge — the same pill used for the reflection status
 * (NOT STARTED / IN PROGRESS / COMPLETED) and the Reading / Tasks "Reviewed"
 * marker, so every accordion-row badge reads identically. Matches PrepStepCard /
 * the coach agenda pill: radius xl, 12px/600, +1px nudge, 0.6 tracking.
 */
function GrailBadge({
  label,
  color,
  filled = true,
}: {
  label: string;
  color: string;
  filled?: boolean;
}) {
  return (
    <Badge
      color={color}
      variant={filled ? 'filled' : 'light'}
      radius="xl"
      tt="uppercase"
      styles={{
        root: { letterSpacing: '0.6px' },
        label: { fontSize: 12, fontWeight: 600, transform: 'translateY(1px)' },
      }}
    >
      {label}
    </Badge>
  );
}

/** Row 1 — Reflect on Prior Commitments (MED-937). */
function PriorCommitmentRow({
  n,
  isLast,
  sessionId,
  commitmentText,
  initialReflection,
  isLocked,
  onCompletenessChange,
}: {
  n: number;
  isLast: boolean;
  sessionId: string | undefined;
  commitmentText: string;
  initialReflection: string;
  isLocked: boolean;
  /**
   * MED-1217 (AC4) — report the LIVE non-empty state of the response on every
   * change so the accordion's outer-collapse gate reads the just-typed value.
   * This is purely outward reporting; the row's own behavior (autosave +
   * load-time collapse) is unchanged (AC8).
   */
  onCompletenessChange: (nonEmpty: boolean) => void;
}) {
  // MED-1114 — load-time-only collapse (Option A): seed collapsed when the
  // response is already answered (return visit); never re-collapses on edit.
  const [open, setOpen] = useState(() => initialReflection.trim().length === 0);
  const [value, setValue] = useState(initialReflection);
  const { state, scheduleSave } =
    usePriorCommitmentReflectionAutosave(sessionId);

  useEffect(() => {
    setValue(initialReflection);
  }, [initialReflection]);

  // MED-1217 (AC4) — keep the accordion's view of this row's completeness in
  // sync with the live textarea value (covers initial mount, edits, and prop
  // resets). Reporting only; no visual effect.
  useEffect(() => {
    onCompletenessChange(value.trim().length > 0);
  }, [value, onCompletenessChange]);

  const handleChange = (text: string) => {
    setValue(text);
    scheduleSave(text);
  };

  const saveHint =
    state === 'saving'
      ? 'Saving…'
      : state === 'saved'
        ? 'All changes saved.'
        : state === 'error'
          ? 'Save failed — try again.'
          : undefined;

  return (
    <AccordionRow
      n={n}
      title="Reflect on Prior Commitments"
      open={open}
      onToggle={() => setOpen(o => !o)}
      isLast={isLast}
    >
      <Text size="sm" c="dimmed">
        From your last session, you committed to:
      </Text>
      <Box
        style={{
          border: `1px solid ${ACCORDION_BORDER}`,
          borderRadius: 8,
          padding: '12px 14px',
          background: '#FFFFFF',
        }}
      >
        <Text size="sm" fs="italic">
          “{commitmentText}”
        </Text>
      </Box>
      <Stack gap={4}>
        <Text
          component="label"
          htmlFor="peer-prior-commitment"
          fw={600}
          size="sm"
        >
          How do you think things went?
        </Text>
        <Textarea
          id="peer-prior-commitment"
          value={value}
          onChange={e => handleChange(e.currentTarget.value)}
          autosize
          minRows={3}
          disabled={isLocked}
        />
        {saveHint && (
          <Text size="xs" c="dimmed" aria-live="polite">
            {saveHint}
          </Text>
        )}
      </Stack>
    </AccordionRow>
  );
}

/**
 * MED-1226 (AC5) — placeholder for "Reflect on Prior Commitments" when prep
 * opened (via the time-based trigger (b)) before the prior session completed.
 * The commitment the participant will reflect on is captured IN that prior
 * session, so it can't exist yet. Rather than hide the section (which would read
 * as "there's nothing here"), we keep it visible and explain that it will be
 * available once the prior session is completed. Defaults open so the message is
 * seen without a click; it has no action and never gates step completion.
 */
function PriorCommitmentPendingRow({ n, isLast }: { n: number; isLast: boolean }) {
  const [open, setOpen] = useState(true);
  return (
    <AccordionRow
      n={n}
      title="Reflect on Prior Commitments"
      open={open}
      onToggle={() => setOpen(o => !o)}
      isLast={isLast}
      titleBadge={<GrailBadge label="Available later" color="gray" filled={false} />}
    >
      <Text size="sm" c="dimmed">
        This section reflects on the commitment you made in your previous
        session. It’ll be available here once that session has been completed —
        there’s nothing to do yet, and the rest of your prep is ready now.
      </Text>
    </AccordionRow>
  );
}

/** Row 2 — Reflection Questions (distinct title so it doesn't echo the step). */
function QuestionsRow({
  n,
  isLast,
  questions,
  status,
  estimatedMinutes,
  isLocked,
  saveHint,
  onChange,
  onSaveAll,
  onSaved,
}: {
  n: number;
  isLast: boolean;
  questions: PreSessionReflectionPayload['questions'];
  status: ReflectionStatus;
  estimatedMinutes: number;
  isLocked: boolean;
  saveHint?: string;
  onChange: (questionId: string, responseText: string) => void;
  onSaveAll: (
    pending: Array<{ question_id: string; response_text: string }>
  ) => Promise<void>;
  /**
   * MED-1217 (AC4) — fired after a successful explicit Save with whether all
   * questions are now answered, so the accordion can (only then) collapse the
   * outer step. Distinct from the row's own collapse, which is unchanged (AC8).
   */
  onSaved: (allAnswered: boolean) => void;
}) {
  const initialValues = useMemo(
    () => Object.fromEntries(questions.map(q => [q.id, q.response_text])),
    [questions]
  );
  const [values, setValues] = useState<Record<string, string>>(initialValues);
  // Seed collapsed when already complete (no flash); collapse on Save when
  // complete; never collapse mid-edit (mirrors MED-1113).
  const [open, setOpen] = useState(() => status !== 'COMPLETED');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const handleChange = (questionId: string, text: string) => {
    setValues(prev => ({ ...prev, [questionId]: text }));
    onChange(questionId, text);
  };

  const handleSave = async () => {
    const pending = questions.map(q => ({
      question_id: q.id,
      response_text: values[q.id] ?? '',
    }));
    setSaving(true);
    try {
      await onSaveAll(pending);
      const allAnswered =
        questions.length > 0 &&
        questions.every(q => (values[q.id] ?? '').trim().length > 0);
      if (allAnswered) setOpen(false);
      // MED-1217 (AC4) — report the explicit-Save outcome so the accordion can
      // collapse the OUTER step only when every present inner row is complete.
      onSaved(allAnswered);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AccordionRow
      n={n}
      title="Reflection Questions"
      open={open}
      onToggle={() => setOpen(o => !o)}
      isLast={isLast}
      titleBadge={
        <GrailBadge
          label={STATUS_LABELS[status]}
          color={STATUS_COLORS[status]}
          filled={status !== 'NOT_STARTED'}
        />
      }
      headerRight={
        <Text size="sm" c="dimmed" visibleFrom="sm">
          {questions.length} {questions.length === 1 ? 'question' : 'questions'}{' '}
          · about {estimatedMinutes} minutes
        </Text>
      }
    >
      <Text size="sm">
        Take a few minutes to reflect before your session. Your responses are
        shared anonymously with your group leader to shape the conversation.
      </Text>
      {questions.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          No reflection questions have been configured for this session yet.
        </Text>
      ) : (
        questions.map(q => (
          <Stack key={q.id} gap={4}>
            <Text
              component="label"
              htmlFor={`peer-q-${q.id}`}
              fw={600}
              size="sm"
            >
              {q.prompt}
            </Text>
            <Textarea
              id={`peer-q-${q.id}`}
              placeholder={q.placeholder_text ?? ''}
              value={values[q.id] ?? ''}
              onChange={e => handleChange(q.id, e.currentTarget.value)}
              autosize
              minRows={3}
              disabled={isLocked}
            />
          </Stack>
        ))
      )}
      {!isLocked && (
        <Group justify="space-between" align="center">
          <Box>
            {saveHint && (
              <Text size="xs" c="dimmed" aria-live="polite">
                {saveHint}
              </Text>
            )}
          </Box>
          <Button
            onClick={handleSave}
            loading={saving}
            variant="filled"
            color="dark"
          >
            Save
          </Button>
        </Group>
      )}
    </AccordionRow>
  );
}

/** Row 3 — Reading / Tasks with Mark as Reviewed (MED-1115). */
function ReadingRow({
  n,
  isLast,
  content,
  reviewedAt,
  onMarkReviewed,
  onReviewed,
}: {
  n: number;
  isLast: boolean;
  content: string;
  reviewedAt: string | null;
  onMarkReviewed: () => Promise<void>;
  /**
   * MED-1217 (AC4) — fired after a successful Mark as Reviewed so the accordion
   * can (only then) collapse the outer step if every present inner row is now
   * complete. The row's own collapse is unchanged (AC8).
   */
  onReviewed: () => void;
}) {
  const reviewed = reviewedAt != null;
  const [open, setOpen] = useState(() => !reviewed);
  const [marking, setMarking] = useState(false);

  const handleMark = async () => {
    setMarking(true);
    try {
      await onMarkReviewed();
      setOpen(false);
      // MED-1217 (AC4) — explicit completion action; let the accordion evaluate
      // the outer-step collapse now that this row is reviewed.
      onReviewed();
    } finally {
      setMarking(false);
    }
  };

  return (
    <AccordionRow
      n={n}
      title="Reading / Tasks"
      open={open}
      onToggle={() => setOpen(o => !o)}
      isLast={isLast}
      titleBadge={
        reviewed ? (
          <GrailBadge label="Reviewed" color="medleyGreen" />
        ) : undefined
      }
    >
      <RichTextContent content={content} />
      {!reviewed && (
        <Group justify="flex-end">
          <Button
            onClick={handleMark}
            loading={marking}
            variant="filled"
            color="dark"
            data-testid="peer-reading-tasks-mark-reviewed"
          >
            Mark as Reviewed
          </Button>
        </Group>
      )}
    </AccordionRow>
  );
}

export default PeerLeaderReflectionAccordion;
